<template>
  <div class="mx-auto my-0 max-w-4xl">
    <div
      v-if="
        checkoutStore.selectedRoles.length !== 0 ||
        (checkoutStore.matchingCriteria.rids &&
          checkoutStore.matchingCriteria.rids.length !== 0) ||
        checkoutStore.roles.length !== 0
      "
      class="mx-auto my-0 max-w-4xl"
    >
      <h2 class="mt-9 text-left text-xl font-semibold text-black">
        Based on your requirements, here are the role(s) that we recommend:
      </h2>
      <p class="text-left">Select as many as you like and click NEXT</p>
      <p
        :class="{ invisible: !error }"
        class="text-center text-sm text-red-500"
      >
        Please select one or more roles to proceed
      </p>
      <div
        v-if="checkoutStore.roles.length !== 0"
        class="mx-auto flex max-w-4xl flex-wrap place-content-center gap-5 py-3"
      >
        <div
          v-for="role in mappedRoles"
          :key="role.id"
          class="mb-5 flex cursor-pointer flex-row items-center justify-between rounded-2xl bg-white p-7 shadow-md md:gap-x-28"
          @click="toggleSelectedRoles(role)"
        >
          <div class="flex flex-col justify-between md:flex-row md:gap-x-28">
            <p class="text-vgocean w-20 font-bold">{{ role.value }}</p>
            <div>
              <p class="text-vgstone text-sm">
                {{ role.description }}
              </p>
              <template v-for="(criteria, criteriaName) in role.keywordsMap">
                <template
                  v-for="(keyword, index) in criteria"
                  :key="keyword + index"
                >
                  <v-chip
                    class="m-1 px-7 py-3 text-black"
                    pill
                    :class="computedColorPerCriteria(criteriaName)"
                  >
                    <span class="truncate">{{ keyword }}</span>
                  </v-chip>
                </template>
              </template>
            </div>
          </div>
          <fa-icon
            class="text-vgmedturq text-xl"
            :icon="
              roleSelected(role)
                ? 'fa-regular fa-square-check'
                : 'fa-regular fa-square'
            "
            @click.stop="toggleSelectedRoles(role)"
          />
        </div>
      </div>
      <div
        v-if="checkoutStore.selectedRoles.length === 0"
        class="mx-auto my-0 max-w-md text-center text-sm"
      >
        <span class="font-bold">Not seeing what you need?</span>
        <p>Click BACK and add or change your criteria.</p>
        <p>
          Or,
          <router-link to="/client/contact">book a call</router-link> with our
          Solutions team if you’d like some help fine-tuning your requirements.
        </p>
      </div>
    </div>
    <div
      v-if="
        checkoutStore.roles.length === 0 &&
        checkoutStore.localRoles.length !== 0
      "
    >
      <p class="text-center font-bold text-black">
        Oops, we don't recognize what you're asking for. Here's some common
        roles.
      </p>
      <div class="mx-auto my-0 flex max-w-2xl flex-wrap justify-center">
        <div
          v-for="role in checkoutStore.localRoles"
          :key="role.id"
          class="mb-5 flex flex-row items-center justify-between rounded-md bg-white p-7 shadow-md md:gap-x-28"
          @click="toggleSelectedRoles(role)"
        >
          <div class="flex flex-col justify-between md:flex-row md:gap-x-28">
            <p class="text-vgocean w-20 font-bold">{{ role.value }}</p>
            <p class="text-vgstone text-sm">
              {{ role.description }}
            </p>
          </div>
          <fa-icon
            class="text-vgmedturq text-xl"
            :icon="
              roleSelected(role)
                ? 'fa-regular fa-square-check'
                : 'fa-regular fa-square'
            "
            @click.stop="toggleSelectedRoles(role)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="
        checkoutStore.selectedRoles.length > 0 &&
        checkoutStore.flattedEnteredKeywords.length != 0
      "
      class="pb-8"
    >
      <p class="text-center text-sm">
        If you’d like more suggestions, click BACK and tell us more.
      </p>
    </div>
    <DeleteRoleDialog ref="deleteRoleDialog" />
  </div>
</template>
<script>
import { mapStores } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import DeleteRoleDialog from '@/components/serviceSelection/DeleteRoleDialog.vue';

export default {
  name: 'Roles',
  components: {
    DeleteRoleDialog,
  },
  props: {
    error: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapStores(useCheckoutStore),
    mappedRoles() {
      return this.checkoutStore.roles.map((role) => ({
        ...role,
        keywordsMap: {
          skills: role.keywords?.filter((keyword) =>
            this.checkoutStore.enteredKeywords.skills?.includes(keyword)
          ),
          tools: role.keywords?.filter((keyword) =>
            this.checkoutStore.enteredKeywords.tools?.includes(keyword)
          ),
        },
      }));
    },
  },
  async created() {
    this.checkoutStore.loadSelectedKeywords();
    if (this.checkoutStore.flattedEnteredKeywords.length !== 0) {
      await this.checkoutStore.searchRoles();
    }
    if (
      this.checkoutStore.roles.length === 1 &&
      this.checkoutStore.selectedRoles.length === 0
    ) {
      this.toggleSelectedRoles(this.checkoutStore.roles[0]);
    }
  },
  methods: {
    roleSelected(role) {
      return this.checkoutStore.selectedRoles.find(
        (selectedRole) => selectedRole.id === role.id
      );
    },
    async toggleSelectedRoles(role) {
      //check if role exists in the cart
      if (
        this.checkoutStore.matchingCriteria?.cart?.some(
          (cartItem) =>
            cartItem.assistant_id !== -1 &&
            parseInt(cartItem.role_id) === parseInt(role.id)
        ) &&
        this.checkoutStore.selectedRoles?.some(
          (selectedRole) => selectedRole.id === parseInt(role.id)
        )
      ) {
        this.$refs.deleteRoleDialog.open(role);
      } else {
        this.checkoutStore.toggleSelectedRoles(role);
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          cart: this.checkoutStore.matchingCriteria.cart
            ? this.checkoutStore.matchingCriteria.cart.filter(
                (cartItem) => parseInt(cartItem.role_id) !== parseInt(role.id)
              )
            : [],
        });
      }
    },
    computedColorPerCriteria(criteria) {
      switch (criteria) {
        case 'roles':
          return 'bg-vgsoftblue';
        case 'skills':
          return 'bg-vgsoftpurple';
        case 'tools':
          return 'bg-vgsoftcyan';
        default:
          return 'bg-vgsoftblue';
      }
    },
  },
};
</script>
